import "./Header.css";
import logoEn from "assets/yelo_plus_en_logo.png";
import logoAr from "assets/yelo_plus_ar.png";
import { Container, Grid, Stack } from "@mui/material";
import AuthService from "services/auth.service";
import useEffectOnce from "hooks/useEffectOnce";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import BasicMenu from "components/atoms/BasicMenu/BasicMenu";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { FaAngleDown } from "react-icons/fa";
import { useCallback } from "react";
import { MODAL, modalActions } from "redux/slices/modal";


interface ILink {
  to: string;
  text: string;
  active: string;
}

export default function Header() {
  const { t, i18n } = useTranslation("translation");

  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = location;
  const user = useAppSelector((state) => state.auth.user);

  const changeLang = useCallback((lng: any) => {
    i18n.changeLanguage(lng);
  }, [i18n])

  const arabicLang = useCallback(() => {
    localStorage.setItem("lang", 'ar')
    changeLang('ar');
    document.body.style.direction = "rtl";
    document.body.style.fontFamily = "arabic, sans-serif";
  }, [changeLang])

  const englishLang = useCallback(() => {
    localStorage.setItem("lang", 'en')
    changeLang('en');
    document.body.style.direction = "ltr";
    document.body.style.fontFamily = "arabic, sans-serif";
  }, [changeLang])

  useEffectOnce(() => {
    const saved = localStorage.getItem("lang");
    if (saved === 'ar') {
      document.body.style.direction = "rtl";
      document.body.style.fontFamily = "arabic, sans-serif";
      i18n.changeLanguage('ar');
    } else {
      document.body.style.direction = "ltr";
      document.body.style.fontFamily = "arabic, sans-serif";
      i18n.changeLanguage('en');
    }
  })

  const links: ILink[] = [
    { to: "/bookings", text: t("bookings"), active: "booking" },
    { to: "/customers", text: t("guests"), active: "customer" },
    { to: "/invoices", text: t("invoices"), active: "invoice" },
    ...(!user?.company_id ? [{ to: "/account-access", text: t("account_access"), active: "account-access" }] : []),
  ];

  const userslinks: ILink[] = [
    { text: user?.first_name + " " + user?.last_name, to: "/profile", active: "" },
    { text: t("logout"), to: '/logout', active: "" },
  ]


  if (
    !user ||
    pathname === "/" ||
    pathname === "/password-otp" ||
    pathname === "/reset-password" ||
    pathname === "/forgot-password"
  )
    return <></>;

  return (
    <header>
      <div className="navbar">
        <Container maxWidth="xl">
          <Grid container alignItems="center">
            {/* Logo — left */}
            <Grid item xs={6} md={3}>
              <Link to="/bookings">
                <div className={i18n.language === "en" ? "logo" : "logo arLogo"}>
                  <img
                    src={i18n.language === "ar" ? logoAr : logoEn}
                    alt="logo"
                    loading="lazy"
                    style={{ maxHeight: "40px", width: "auto", objectFit: "contain" }}
                  />
                </div>
              </Link>
            </Grid>

            {/* Nav links — truly centered */}
            <Grid item md={6} sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center" }}>
              <div className="navbar-nav">
                <ul>
                  {links.map(({ to, text, active }, i) => (
                    <li key={i}>
                      <Link
                        to={to}
                        className={pathname.includes(active) ? "active" : ""}>
                        {text}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Grid>

            {/* Actions — right */}
            <Grid item xs={6} md={3} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <div className="navbar-actions">
                <BasicMenu
                  list={[
                    { text: "En", onClick: englishLang },
                    { text: "عربي", onClick: arabicLang },
                  ]}
                  avatar={false}
                  color="#ffffff"
                >
                  <Stack direction="row" alignItems="center" columnGap={1} sx={{ fontFamily: "arabic, sans-serif", fontSize: "15px", fontWeight: 400 }}>
                    <span>{i18n.language === "en" ? "EN" : "عربي"}</span>
                    <FaAngleDown />
                  </Stack>
                </BasicMenu>
                <BasicMenu
                  text={(user.first_name?.charAt(0) || "") + (user.last_name?.charAt(0) || "")}
                  list={userslinks.map(({ text, to }: any) => ({
                    text: text,
                    onClick: () =>
                      to === "/logout"
                        ? AuthService.logout()
                        : navigate(to),
                  }))}
                  avatar={true}
                />
              </div>
            </Grid>

            {/* Mobile nav links — full width row below logo/actions */}
            <Grid item xs={12} sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", pb: 1 }}>
              <div className="navbar-nav">
                <ul>
                  {links.map(({ to, text, active }, i) => (
                    <li key={i}>
                      <Link
                        to={to}
                        className={pathname.includes(active) ? "active" : ""}>
                        {text}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Grid>
          </Grid>
        </Container>
      </div>
    </header>
  );
}
